import type { BnblRoomPackage } from 'actions/types/offerActionTypes'
import type { PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import type { IntlShape } from 'react-intl'
import { groupBy } from 'lib/array/arrayUtils'
import { allRoomsOccupantsFitRoomCapacity } from 'lib/checkout/cartReservationUtils'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { isSameCancellationOption } from 'lib/offer/cancellationOptionPricing'
import { multiplyPricing } from 'lib/offer/pricing'
import config from 'constants/config'

// bnbl constants, enums, types

export const DEFAULT_OCCUPANCY_FOR_BNBL: App.Occupants = { adults: 1 }

/**
 * Single source of truth for the base Buy-Now-Book-Later eligibility check the real Flash CTA
 * gate uses (`FlashHotelOptionCTAs.tsx`'s `showBNBL`) -- deliberately excludes the
 * wedding-gift-registry exclusion, which is a UI-only concern with no equivalent signal available
 * to non-component callers.
 */
export function isBuyNowBookLaterAvailableForPackage({
  capacityValid,
  hasBuyNowBookLater,
  allowBuyNowBookLater,
}: {
  capacityValid: boolean
  hasBuyNowBookLater: boolean
  allowBuyNowBookLater: boolean | undefined
}): boolean {
  return !!(capacityValid && hasBuyNowBookLater && allowBuyNowBookLater && config.accommodation.hasBnbl)
}

export enum BnblSteps {
  HOME = 'home',
  GIFT = 'gift',
  CHANGE_DURATION = 'change-duration',
  CHANGE_PACKAGE = 'change-package',
}

export type CheckboxStateForSwitchablePackageDurations = {
  [duration: string]: boolean
}

export type BnblSwitchablePackageDuration = {
  isSelected: boolean
  package: App.Package
  capacityValid: boolean
}

export type BnblTotalPackagesPrices = {
  hotelPrice: number
  priceFrom: number
  hotelValue: number
  shouldDisplayValue: boolean
  hotelMemberPrice: number
  memberPriceFrom: number
}

// bnbl modal prop utils

export function getBnblOccupancies(rooms: Array<App.Occupants> = []): Array<App.Occupants> {
  return rooms.map(({ adults, children, childrenAge, infants, teenagers }) => ({
    adults,
    children,
    childrenAge,
    infants,
    teenagers,
  }))
}

export function getBnblOccupanciesByRoomIds(
  rooms: Array<App.Occupants & { id: string }>,
  roomIds: ReadonlySet<string>,
): Array<App.Occupants> {
  return getBnblOccupancies(rooms.filter(room => roomIds.has(room.id)))
}

export function getModalTitle(step: BnblSteps, intl: IntlShape) {
  switch (step) {
    case BnblSteps.HOME: {
      return intl.formatMessage({
        id: 'yj2dpIBW6Ll_ZRSSyA4F',
        defaultMessage: 'Buy now, choose dates later',
        description: 'Modal title for the buy now book later initial home step',
      })
    }
    case BnblSteps.GIFT: {
      return intl.formatMessage({
        id: 'cEDEXN99sp2_PTMS9yO_',
        defaultMessage: 'Give this package as a gift',
        description: 'Modal title for the gift option in buy now book later flow',
      })
    }
    case BnblSteps.CHANGE_DURATION: {
      return intl.formatMessage({
        id: 'IyMJ5bKxmpEGN1OUo1Sf',
        defaultMessage: 'How long do you want to stay?',
        description: 'Modal title for changing stay duration in buy now book later flow',
      })
    }
    case BnblSteps.CHANGE_PACKAGE: {
      return intl.formatMessage({
        id: 'Xv_tTMFmdUvcsUg5r2ns',
        defaultMessage: 'Choose your package',
        description: 'Modal title for choosing a package in buy now book later flow',
      })
    }
  }
}

export function getOnBack(
  step: BnblSteps,
  setStep: (step: BnblSteps) => void,
  setSelectedPackageUniqueKey: () => void,
  isGift: boolean,
) {
  switch (step) {
    case BnblSteps.HOME:
    case BnblSteps.GIFT:
      return undefined
    case BnblSteps.CHANGE_DURATION:
    case BnblSteps.CHANGE_PACKAGE:
      if (isGift) {
        return () => {
          setStep(BnblSteps.GIFT)
          setSelectedPackageUniqueKey()
        }
      }
      return () => {
        setStep(BnblSteps.HOME)
        setSelectedPackageUniqueKey()
      }
  }
}

// change bnbl package duration utils

function getAllPackageOptions(offer: App.HotelOffer, currentPackageUniqueKey: string) {
  return offer.packages.map((packageOption) => {
    return {
      capacityValid: packageOption.roomType && allRoomsOccupantsFitRoomCapacity(DEFAULT_OCCUPANCY_FOR_BNBL, packageOption.roomRate.capacities, offer),
      isSelected: packageOption.uniqueKey === currentPackageUniqueKey,
      package: packageOption,
    }
  })
}

export function bnblSwitchablePackageDurations(offer: App.HotelOffer, currentPackage: App.HotelPackage): Array<BnblSwitchablePackageDuration> {
  const allPackageOptions = getAllPackageOptions(offer, currentPackage.uniqueKey)
    .filter(option => isSameCancellationOption(option.package, currentPackage))
  const allPackageOptionsGroupedByDuration = groupBy(allPackageOptions, o => o.package.duration)
  const switchableOptions = Array.from(allPackageOptionsGroupedByDuration.values()).map(options => {
    // try find the different night version of the currently selected room
    const matchingRoomOption = options.find(o => o.capacityValid && o.package.roomType.id === currentPackage.roomType.id)
    // always take the package that's selected if there is one
    // otherwise try match the room option, then the first one that's valid or just the first one if none are valid
    return options.find(o => o.isSelected) || matchingRoomOption || options.find(o => o.capacityValid) || options[0]
  })
  return switchableOptions
}

export function getBnblPackageViewByNumberOfRooms(
  packageView: App.OfferPackageOptionView,
  numberOfRooms: number,
): App.OfferPackageOptionView {
  const hotelPrice = packageView.hotelPrice * numberOfRooms
  const hotelMemberPrice = packageView.hotelMemberPrice * numberOfRooms
  const hotelValue = packageView.hotelValue * numberOfRooms
  const taxesAndFees = packageView.taxesAndFees * numberOfRooms
  const propertyFees = packageView.propertyFees * numberOfRooms
  const hotelDiscountPercent = getValueOffPercent(hotelValue, hotelPrice)
  const pricing = multiplyPricing(packageView.pricing, numberOfRooms)
  const memberPricing = packageView.memberPricing ? multiplyPricing(packageView.memberPricing, numberOfRooms) : undefined
  const pricingWithFlights = packageView.pricingWithFlights ? multiplyPricing(packageView.pricingWithFlights, numberOfRooms) : undefined
  const memberPricingWithFlights = packageView.memberPricingWithFlights ? multiplyPricing(packageView.memberPricingWithFlights, numberOfRooms) : undefined

  return {
    ...packageView,
    hotelPrice,
    hotelMemberPrice,
    hotelValue,
    taxesAndFees,
    propertyFees,
    hotelDiscountPercent,
    pricing,
    memberPricing,
    pricingWithFlights,
    memberPricingWithFlights,
  }
}

/** Build a complete BNBL selection atomically, preserving each package's real guest slots. */
export function resolveMultiRoomBnblPackages(
  rooms: Array<App.Occupants & { id: string }>,
  packagesByRoom: PackageOptionViewByRoom,
  basePackageViews: ReadonlyArray<App.OfferPackageOptionView>,
): { ok: true; packages: Array<BnblRoomPackage> } | { ok: false; reason: 'incomplete-selection' | 'package-unavailable' } {
  const selected: Array<[string, App.OfferPackageOptionView]> = []
  for (const room of rooms) {
    const view = packagesByRoom[room.id]
    if (!view) return { ok: false, reason: 'incomplete-selection' }
    selected.push([room.id, view])
  }
  if (!selected.length) return { ok: false, reason: 'incomplete-selection' }
  const packages: Array<BnblRoomPackage> = []
  for (const [key, entries] of groupBy(selected, ([, view]) => view.package.uniqueKey)) {
    const base = basePackageViews.find(view => view.package.uniqueKey === key)
    if (!base) return { ok: false, reason: 'package-unavailable' }
    packages.push({
      packageView: getBnblPackageViewByNumberOfRooms(base, entries.length),
      numberOfRooms: entries.length,
      occupancies: getBnblOccupanciesByRoomIds(rooms, new Set(entries.map(([roomId]) => roomId))),
    })
  }
  return { ok: true, packages }
}
